<script>
	import { createEventDispatcher, tick } from "svelte";
	import { nanoid } from "nanoid";

	export let optionList = [];
	export let selectedItems = [];
	export let otherOption = null;
	export let label = "Select items";
	export let placeholder = "Type to search...";
	export let labelProperty = "name";
	export let valueProperty = "id";
	export let maxSelections = null;
	export let required = false;
	export let disabled = false;
	export let id = `msac-${nanoid(5)}`;

	// --- Internal State ---
	let validator = null;
	let inputValue = "";
	let filteredItems = [];
	let showSuggestions = false;
	let activeIndex = -1; // For keyboard navigation
	let inputElement; // To bind to the input element

	const dispatch = createEventDispatcher();

	// --- Computed Properties ---
	$: selectedValues = selectedItems.map((item) => item[valueProperty]);
	$: canAddMore = !maxSelections || selectedItems.length < maxSelections;

	// --- Functions ---
	function filterItems() {
		if (!inputValue) {
			filteredItems = [];
			showSuggestions = false;
			return;
		}

		const lowerCaseInput = inputValue.toLowerCase();
		filteredItems = [
			otherOption,
			...optionList.filter(
				(item) =>
					item[labelProperty].toLowerCase().includes(lowerCaseInput) &&
					item[valueProperty] !== otherOption[valueProperty] &&
					!selectedValues.includes(item[valueProperty])
			)
		];

		showSuggestions = filteredItems.length > 0;
		activeIndex = -1; // Reset keyboard navigation index
	}

	async function selectItem(item) {
		if (!canAddMore || disabled) return;

		if (!selectedItems.includes(item)) {
			selectedItems = [...selectedItems, item];
		}

		//inputValue = "";
		//filteredItems = [];
		//showSuggestions = false;
		activeIndex = -1;
		dispatch("change", { selectedItems }); // Dispatch change event
		inputElement?.focus(); // Keep focus on input

		// Wait for the DOM to update after selectedItems changes
		await tick();

		// trigger change for validation
		if (validator) {
			const event = new Event("change", { bubbles: true }); // Ensure event bubbles up if needed
			validator.dispatchEvent(event);
		}
	}

	async function removeItem(itemToRemove) {
		if (disabled) return;
		selectedItems = selectedItems.filter((item) => item[valueProperty] !== itemToRemove[valueProperty]);
		dispatch("change", { selectedItems }); // Dispatch change event
		filterItems(); // Re-filter in case the removed item now matches input

		// Wait for the DOM to update after selectedItems changes
		await tick();

		// trigger change for validation
		if (validator) {
			const event = new Event("change", { bubbles: true }); // Ensure event bubbles up if needed
			validator.dispatchEvent(event);
		}
	}

	function handleInput(event) {
		inputValue = event.target.value;
		filterItems();
	}

	function handleKeyDown(event) {
		if (disabled) return;

		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				if (showSuggestions && filteredItems.length > 0) {
					activeIndex = (activeIndex + 1) % filteredItems.length;
				}
				break;
			case "ArrowUp":
				event.preventDefault();
				if (showSuggestions && filteredItems.length > 0) {
					activeIndex = (activeIndex - 1 + filteredItems.length) % filteredItems.length;
				}
				break;
			case "Enter":
				event.preventDefault();
				if (showSuggestions && activeIndex >= 0 && activeIndex < filteredItems.length) {
					selectItem(filteredItems[activeIndex]);
				} else if (filteredItems.length === 1) {
					// Select if only one option remains and user presses Enter
					selectItem(filteredItems[0]);
				}
				break;
			case "Escape":
				showSuggestions = false;
				activeIndex = -1;
				break;
			case "Backspace":
				// Remove last item if input is empty and backspace is pressed
				if (inputValue === "" && selectedItems.length > 0) {
					removeItem(selectedItems[selectedItems.length - 1]);
				}
				break;
		}
	}

	function handleBlur(event) {
		// Delay hiding suggestions to allow click events on suggestions to register
		setTimeout(() => {
			inputValue = "";
			showSuggestions = false;
			activeIndex = -1;
		}, 150);
	}

	function handleFocus() {
		if (inputValue) {
			filterItems(); // Show suggestions again on focus if there's input
		}
	}

	// Reactive statement to clear input if max selections reached
	$: if (!canAddMore && inputValue) {
		inputValue = "";
		filteredItems = [];
		showSuggestions = false;
	}
</script>

<div class="multi-select-autocomplete" on:focusout={handleBlur}>
	{#if label}
		<!-- svelte-ignore a11y-label-has-associated-control -->
		<label for={id}>
			{label}
			{#if required}
				<span class="required">*</span>
			{/if}
		</label>
	{/if}

	<div class="selected-items-container" style={disabled ? "padding-top: .75rem;" : null}>
		{#if !disabled}
			<input
				type="text"
				bind:this={inputElement}
				bind:value={inputValue}
				{id}
				{placeholder}
				{disabled}
				readonly={!canAddMore}
				class="form-control autocomplete-input"
				autocomplete="off"
				aria-autocomplete="list"
				aria-controls="{id}-suggestions"
				aria-activedescendant={activeIndex >= 0 ? `${id}-suggestion-${activeIndex}` : undefined}
				on:input={handleInput}
				on:keydown={handleKeyDown}
				on:focus={handleFocus} />
		{/if}

		{#each selectedItems as item (item[valueProperty])}
			<span class="selected-item">
				{item[labelProperty]}
				{#if !disabled}
					<button
						type="button"
						class="remove-item"
						on:click={() => removeItem(item)}
						aria-label="Remove {item[labelProperty]}"
						{disabled}>
						&times;
					</button>
				{/if}
			</span>
		{/each}
	</div>

	{#if !disabled && showSuggestions && filteredItems.length > 0}
		<ul class="suggestions-list" id="{id}-suggestions" role="listbox">
			{#each filteredItems as item, index (item[valueProperty])}
				<li
					role="option"
					id="{id}-suggestion-{index}"
					class:active={index === activeIndex}
					on:mousedown|preventDefault={() => selectItem(item)}
					aria-selected={index === activeIndex}>
					{item[labelProperty]}
				</li>
			{/each}
		</ul>
	{/if}

	<!-- Hidden input for potential form validation integration -->
	<input
		bind:this={validator}
		type="text"
		hidden
		{required}
		value={selectedItems.length > 0 ? "selected" : ""}
		aria-hidden="true"
		tabindex="-1"
		data-bouncer-message="Please select at least one item." />
</div>

<style>
	.multi-select-autocomplete {
		position: relative;
		width: 100%;
	}

	:global(.multi-select-autocomplete.invalid .selected-items-container) {
		border: 2px solid var(--color-error) !important;
	}

	:global(.multi-select-autocomplete.invalid input) {
		border: none !important;
	}

	.selected-items-container {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		background-color: #fff;
		border: 1px solid var(--color-border, #ced4da);
		padding: 0 0.75rem;
		min-height: calc(1.5em + 0.75rem + 2px);
		gap: 0.3rem;
		cursor: text;
	}
	.selected-items-container:focus-within {
		outline: solid 2px rgb(26, 26, 26);
		border-radius: 4px;
	}

	.selected-item {
		display: inline-flex;
		align-items: center;
		background-color: var(--color-primary-light, #e0e0e0);
		color: var(--color-body);
		padding: 0.2em 0.6em;
		margin-bottom: 0.75rem;
		border-radius: var(--border-radius-sm, 0.2rem);
		font-size: 0.9em;
		white-space: nowrap;
	}

	.remove-item {
		background: none;
		border: none;
		color: inherit;
		cursor: pointer;
		font-size: 1.2em;
		line-height: 1;
		margin-left: 0.4em;
		padding: 0;
		opacity: 0.7;
	}
	.remove-item:hover,
	.remove-item:focus {
		opacity: 1;
	}
	.remove-item:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}

	.autocomplete-input {
		flex-grow: 1; /* Take remaining space */
		border: none;
		outline: none;
		padding: 0;
		min-width: 100px;
		background-color: transparent;
		box-shadow: none;
	}
	.autocomplete-input:focus {
		box-shadow: none;
	}
	.autocomplete-input[readonly] {
		cursor: default;
		background-color: transparent;
	}

	.suggestions-list {
		position: absolute;
		background-color: white;
		border: 1px solid var(--color-border, #ced4da);
		border-top: none;
		border-radius: 0 0 var(--border-radius, 0.25rem) var(--border-radius, 0.25rem);
		list-style: none;
		margin: 0;
		padding: 0;
		max-height: 200px;
		overflow-y: auto;
		width: 100%;
		z-index: 10;
		box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	}

	.suggestions-list li {
		padding: 0.5rem 0.75rem;
		cursor: pointer;
	}

	.suggestions-list li:hover,
	.suggestions-list li.active {
		background-color: var(--color-primary-hover, #f0f0f0);
		color: var(--color-primary, #0d6efd);
	}
</style>
